import { ThumbsDown, ThumbsUp } from "lucide-react";
import { Tooltip } from "@merge-api/merge-javascript-shared";
import { Message } from "@merge-api/merge-javascript-shared/dist/designSystem/molecules/ChatWidget/types";
import { useLlmResponseFeedback } from "../hooks/useLlmResponseFeedback";

/**
 * Creates a function that generates message-specific feedback tools
 * Each message gets its own isolated instance of feedback controls
 */
export const createMessageToolsFactory = () => {
  // This returns a function that will be called for each message
  return (message: Message) => {
    if (message.sender !== "response" || !message.text) return null;

    const responseId = message.props?.response_id;

    return <MessageTools responseId={responseId} />;
  };
};

interface MessageToolsProps {
  responseId: string;
}

/**
 * Message-specific feedback tools component that uses the useLlmResponseFeedback hook
 */
const MessageTools = ({ responseId }: MessageToolsProps) => {
  const {
    feedbackState,
    isSubmitting,
    handleHelpfulFeedback,
    handleNotHelpfulFeedback,
  } = useLlmResponseFeedback();

  return (
    <div className="flex space-x-2 items-center">
      <Tooltip title="Good response">
        <ThumbsUp
          size={16}
          className={`hover:cursor-pointer ${
            feedbackState === null && !isSubmitting
              ? "hover:text-gray-50"
              : feedbackState === "HELPFUL"
              ? "text-green-40"
              : "text-gray-60 cursor-not-allowed"
          }`}
          onClick={() => {
            if (feedbackState === null && !isSubmitting) {
              handleHelpfulFeedback(responseId);
            }
          }}
        />
      </Tooltip>
      <Tooltip title="Bad response">
        <ThumbsDown
          size={16}
          className={`hover:cursor-pointer ${
            feedbackState === null && !isSubmitting
              ? "hover:text-gray-50"
              : feedbackState === "NOT_HELPFUL"
              ? "text-red-40"
              : "text-gray-60 cursor-not-allowed"
          }`}
          onClick={() => {
            if (feedbackState === null && !isSubmitting) {
              handleNotHelpfulFeedback(responseId);
            }
          }}
        />
      </Tooltip>
    </div>
  );
};
